import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { useUserInitQuery, PendingPolicy } from '@/hooks/queries/useUserInitQuery';
import { useQueryClient } from '@tanstack/react-query';
import { detectInAppBrowser } from '@/lib/in-app-browser';
import { isInAppBrowserOverlayDismissed } from '@/components/ProtectedRoute';

interface PolicyAcceptanceContextType {
  showPolicyAcceptanceModal: boolean;
  setShowPolicyAcceptanceModal: (show: boolean) => void;
  handlePolicyAccepted: () => void;
  handlePolicyDeclined: () => void;
  pendingPolicies: PendingPolicy[];
}

const PolicyAcceptanceContext = createContext<PolicyAcceptanceContextType | undefined>(undefined);

export const usePolicyAcceptance = () => {
  const context = useContext(PolicyAcceptanceContext);
  if (context === undefined) {
    throw new Error('usePolicyAcceptance must be used within a PolicyAcceptanceProvider');
  }
  return context;
};

export const PolicyAcceptanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  const queryClient = useQueryClient();
  const [hasRequestedModal, setHasRequestedModal] = useState(false);

  // Use the unified user init query - this data is already being fetched
  // No separate API call needed!
  const { data: userInitData, isSuccess } = useUserInitQuery();
  const pendingPolicies = userInitData?.pendingPolicies || [];

  // Check if we're in an in-app browser
  const isInAppBrowser = useMemo(() => detectInAppBrowser().isInAppBrowser, []);

  // Track if the in-app browser overlay has been dismissed
  const [inAppOverlayDismissed, setInAppOverlayDismissed] = useState(() => {
    return !isInAppBrowser || isInAppBrowserOverlayDismissed();
  });

  // Listen for in-app browser overlay dismissal event
  useEffect(() => {
    if (!isInAppBrowser) return;

    const handleDismissal = () => {
      setInAppOverlayDismissed(true);
    };

    window.addEventListener('inAppBrowserOverlayDismissed', handleDismissal);
    return () => window.removeEventListener('inAppBrowserOverlayDismissed', handleDismissal);
  }, [isInAppBrowser]);

  // Request modal when we have pending policies
  // No longer waits for GeoComply - runs in parallel
  useEffect(() => {
    const shouldCheck = user && 
      isSuccess &&
      !hasRequestedModal && 
      (!isInAppBrowser || inAppOverlayDismissed);
    
    if (shouldCheck && pendingPolicies.length > 0) {
      setHasRequestedModal(true);
      requestShow('POLICY_ACCEPTANCE');
    }
  }, [user, isSuccess, pendingPolicies, hasRequestedModal, requestShow, isInAppBrowser, inAppOverlayDismissed]);

  /**
   * Handle policy acceptance
   */
  const handlePolicyAccepted = useCallback(() => {
    notifyDismissed('POLICY_ACCEPTANCE');
    // Invalidate user init query to refetch pending policies
    queryClient.invalidateQueries({ queryKey: ['user', 'init'] }).finally(() => {
      setHasRequestedModal(false);
    });
  }, [notifyDismissed, queryClient]);

  /**
   * Handle policy decline
   */
  const handlePolicyDeclined = useCallback(() => {
    notifyDismissed('POLICY_ACCEPTANCE');
    logout();
  }, [notifyDismissed, logout]);

  // Reset when user logs out
  useEffect(() => {
    if (!user) {
      setHasRequestedModal(false);
    }
  }, [user]);

  // Get visibility from queue
  const showPolicyAcceptanceModal = shouldShow('POLICY_ACCEPTANCE');

  const value = {
    showPolicyAcceptanceModal,
    setShowPolicyAcceptanceModal: () => {}, // Deprecated - use queue system
    handlePolicyAccepted,
    handlePolicyDeclined,
    pendingPolicies,
  };

  return (
    <PolicyAcceptanceContext.Provider value={value}>
      {children}
    </PolicyAcceptanceContext.Provider>
  );
};
